import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { fetchNotificationsCount } from "../../../../utils/apiUtils";
import { NOTIFICATIONS_QUERY_KEY_ROOT } from "./constants";

const COUNT_QUERY_SEGMENT = "count";

/**
 * Fetches the total number of notifications matching the current queue filters (`GET /notifications/count`).
 * Uses the same filter inputs as the paginated list; no `cursor`. {@link keepPreviousData} avoids flicker when filters change.
 *
 * @param {object} params
 * @param {string} params.sendType
 * @param {string | null} params.queueFilter
 * @param {Record<string, string>} params.columnFilters
 * @returns {{
 *   notificationsCountQueryKey: readonly unknown[],
 *   notificationsTotalCount: number | undefined,
 *   isNotificationsTotalCountLoading: boolean,
 *   isNotificationsTotalCountError: boolean,
 * }}
 */
export function useTotalCount({ sendType, queueFilter, columnFilters }) {
    const notificationsCountQueryKey = useMemo(
        () => [
            NOTIFICATIONS_QUERY_KEY_ROOT,
            COUNT_QUERY_SEGMENT,
            sendType,
            queueFilter,
            columnFilters,
        ],
        [sendType, queueFilter, columnFilters],
    );

    const countQuery = useQuery({
        queryKey: notificationsCountQueryKey,
        queryFn: () =>
            fetchNotificationsCount({
                sendType,
                queueFilter,
                columnFilters,
            }),
        placeholderData: keepPreviousData, // Avoids flashing a loading animation on every filter tweak while the new count loads
    });

    const notificationsTotalCount =
        countQuery.data != null && typeof countQuery.data.count === "number"
            ? countQuery.data.count
            : undefined;

    const isNotificationsTotalCountLoading =
        countQuery.isPending && countQuery.data === undefined;

    const isNotificationsTotalCountError = countQuery.isError;

    return {
        notificationsCountQueryKey,
        notificationsTotalCount,
        isNotificationsTotalCountLoading,
        isNotificationsTotalCountError,
    };
}
